import { useState } from 'react';
import { UserPen, Crown, Loader2, Check, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Premium display-name editor. First change free, then $10 (PayPal) per rename.
export default function DisplayNameCard() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(user?.name || '');
    const [busy, setBusy] = useState(false);
    const [paying, setPaying] = useState(false);

    if (!user || user.is_guest) return null;

    const used = Number(user.name_changes_used || 0);
    const credits = Number(user.name_change_credits || 0);
    const isFounder = !!user.is_founder;
    const canChangeFree = isFounder || used < 1 || credits > 0;

    if (!user.premium && !isFounder) {
        return (
            <button
                data-testid="displayname-upsell"
                onClick={() => navigate('/premium')}
                className="w-full mb-6 flex items-center gap-3 bg-brand-card border border-brand-border rounded-2xl p-4 text-left hover:border-brand-green/40"
            >
                <div className="w-10 h-10 rounded-2xl bg-brand-ochre/15 text-brand-ochre flex items-center justify-center flex-shrink-0"><UserPen size={18} /></div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-ink inline-flex items-center gap-1.5">Choose your own name <Crown size={12} className="text-brand-ochre" /></div>
                    <div className="text-xs text-brand-ink3">Premium feature — use a custom display name instead of your Google name.</div>
                </div>
            </button>
        );
    }

    const save = async () => {
        const name = value.trim();
        if (name.length < 2) { toast.error('Name too short'); return; }
        if (name === user.name) { setEditing(false); return; }
        setBusy(true);
        try {
            await api.patch('/auth/display-name', { name });
            await refresh();
            toast.success('Name updated 🌱');
            setEditing(false);
        } catch (e) {
            if (e?.response?.status === 402) toast.error('Free change used — pay $10 to rename again');
            else toast.error(e?.response?.data?.detail || 'Could not update name');
        } finally { setBusy(false); }
    };

    const payToRename = async () => {
        setPaying(true);
        try {
            const r = await api.post('/account/name-change/order');
            if (r.data?.approve_url) window.location.href = r.data.approve_url;
            else toast.error('Could not start payment');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not start payment');
        } finally { setPaying(false); }
    };

    return (
        <div className="mb-6 bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="displayname-card">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-brand-green/12 text-brand-green flex items-center justify-center flex-shrink-0"><UserPen size={18} /></div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Display name</div>
                    <div className="text-sm font-medium text-brand-ink truncate">{user.name}</div>
                </div>
                {!editing && (
                    <button data-testid="displayname-edit" onClick={() => { setValue(user.name || ''); setEditing(true); }} className="text-sm text-brand-green font-medium px-3 py-1.5 rounded-full hover:bg-brand-bg2">Change</button>
                )}
            </div>

            {editing && (
                <div className="mt-3">
                    {canChangeFree ? (
                        <>
                            <input
                                data-testid="displayname-input"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                maxLength={30}
                                className="w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2.5 text-sm text-brand-ink"
                                placeholder="Your name"
                            />
                            <p className="text-[11px] text-brand-ink3 mt-1.5">
                                {used < 1 ? 'Your first name change is free.' : `You have ${credits} paid rename${credits === 1 ? '' : 's'} available.`}
                            </p>
                            <div className="flex gap-2 mt-2">
                                <button onClick={save} disabled={busy} data-testid="displayname-save" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm font-medium disabled:opacity-50">
                                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
                                </button>
                                <button onClick={() => setEditing(false)} className="px-4 py-2.5 rounded-full text-sm bg-brand-bg2 border border-brand-border text-brand-ink2"><X size={15} /></button>
                            </div>
                        </>
                    ) : (
                        <div className="bg-brand-bg2 border border-brand-border rounded-xl p-3">
                            <p className="text-sm text-brand-ink">You've used your free name change.</p>
                            <p className="text-xs text-brand-ink3 mt-0.5 mb-2">Rename again for a one-time $10.</p>
                            <button onClick={payToRename} disabled={paying} data-testid="displayname-pay" className="w-full inline-flex items-center justify-center gap-2 bg-brand-ochre hover:opacity-90 text-white py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                                {paying ? <Loader2 size={15} className="animate-spin" /> : <Crown size={15} />} Pay $10 to rename
                            </button>
                            <button onClick={() => setEditing(false)} className="w-full text-xs text-brand-ink3 mt-2">Cancel</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
